import {
  attributionFields,
  attributionRetentionMs,
  attributionSignalFields,
  mergeLeadAttribution,
  parseAttributionTouch,
  parseLeadAttribution,
  type AttributionTouch,
  type LeadAttribution,
} from '../../shared/leadAttribution'
import { getGoogleAdsAttribution } from './googleAdsClickIds'
import {
  experimentAttributionFields,
  parseExperimentAttribution,
} from '../../shared/experimentAttribution'

const STORAGE_KEY = 'tf_lead_form_attribution'
const COOKIE_NAME = 'tf_lead_form_attribution'
const TTL_MS = attributionRetentionMs

type CaptureWindow = Window & {
  __tfConsent?: { categories?: { analytics?: boolean } }
  __taxforceExperimentRead?: { document: Document; value: unknown }
  __tfExperiment?: { get: () => unknown }
  __taxforceAttributionPage?: {
    document: Document
    url: string
    touch: AttributionTouch | null
    hostTouch?: AttributionTouch
  }
  __tfAttribution?: { get?: () => unknown }
}

function readSnapshots(): unknown[] {
  const snapshots: unknown[] = []
  try {
    const cookie = document.cookie
      .split('; ')
      .find((part) => part.startsWith(`${COOKIE_NAME}=`))
    if (cookie) {
      snapshots.push(decodeURIComponent(cookie.slice(COOKIE_NAME.length + 1)))
    }
  } catch {
    // A blocked or malformed cookie must not block current-page capture.
  }
  try {
    snapshots.push(window.localStorage.getItem(STORAGE_KEY))
  } catch {
    // Storage can be disabled in embedded or private browsing contexts.
  }
  return snapshots
}

function persist(attribution: LeadAttribution): void {
  const json = JSON.stringify(attribution)
  try {
    window.localStorage.setItem(STORAGE_KEY, json)
  } catch {
    // The cookie remains available when localStorage is blocked.
  }
  const hostname = window.location.hostname
  const domain =
    hostname === 'taxforce.ai' || hostname.endsWith('.taxforce.ai')
      ? '; Domain=.taxforce.ai'
      : ''
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  const cookie = `${COOKIE_NAME}=${encodeURIComponent(json)}`
  // Cookies have a size limit. localStorage can retain longer URL snapshots.
  if (cookie.length + domain.length + 100 <= 4096) {
    try {
      document.cookie = `${cookie}; Path=/; SameSite=Lax; Max-Age=${TTL_MS / 1000}${domain}${secure}`
    } catch {
      // localStorage or the current-page snapshot can still supply the form.
    }
  } else {
    // Do not leave an older cross-subdomain snapshot pretending to be current.
    try {
      document.cookie = `${COOKIE_NAME}=; Path=/; SameSite=Lax; Max-Age=0${domain}${secure}`
    } catch {
      /* Cookies can be blocked. */
    }
  }
}

function capturePage(): AttributionTouch | null {
  const captureWindow = window as CaptureWindow
  const previous = captureWindow.__taxforceAttributionPage
  const url = new URL(window.location.href)
  if (previous?.document === document && previous.url === url.href) {
    return previous.touch
  }
  const fields: Partial<AttributionTouch> = {}
  for (const field of attributionFields) {
    const value = url.searchParams.get(field)
    if (value) fields[field] = value
  }
  // Read the external referrer once per document. SPA navigation must not
  // turn an internal page change into another external referral.
  if (previous?.document !== document && document.referrer) {
    try {
      const referrer = new URL(document.referrer)
      const ownHostname = referrer.hostname === url.hostname
      const ownSite = [referrer.hostname, url.hostname].every(
        (hostname) =>
          hostname === 'taxforce.ai' || hostname.endsWith('.taxforce.ai'),
      )
      if (!ownHostname && !ownSite) fields.referrer = referrer.origin
    } catch {
      // Ignore invalid referrers.
    }
  }
  const touch = parseAttributionTouch({
    ...fields,
    capturedAt: new Date().toISOString(),
    landingUrl: url.href,
    captureMethod: attributionSignalFields.some((field) => fields[field])
      ? 'url'
      : 'referrer',
  })
  captureWindow.__taxforceAttributionPage = { document, url: url.href, touch }
  return touch
}

function sameSignal(a: AttributionTouch, b: AttributionTouch): boolean {
  const clickIds = ['gclid', 'gbraid', 'wbraid', 'fbclid'] as const
  if (clickIds.some((field) => a[field] || b[field])) {
    return clickIds.every((field) => a[field] === b[field])
  }
  if (attributionFields.some((field) => a[field] || b[field])) {
    return attributionFields.every((field) => a[field] === b[field])
  }
  return a.referrer === b.referrer
}

function readHostAttribution(): unknown {
  try {
    return (window as CaptureWindow).__tfAttribution?.get?.()
  } catch {
    return undefined
  }
}

function readHostExperiment(): {
  present: boolean
  failed?: boolean
  known?: boolean
  value?: unknown
} {
  const win = window as CaptureWindow
  const cached =
    win.__taxforceExperimentRead?.document === document
      ? win.__taxforceExperimentRead
      : undefined
  try {
    if (win.__tfConsent?.categories?.analytics === false) {
      win.__taxforceExperimentRead = { document, value: null }
      return { present: true, known: true, value: null }
    }
    const api = win.__tfExperiment
    if (!api)
      return cached
        ? { present: true, known: true, value: cached.value }
        : { present: false }
    if (typeof api.get !== 'function') throw new Error('Host getter missing')
    const value = parseExperimentAttribution(api.get())
    win.__taxforceExperimentRead = { document, value }
    return { present: true, known: true, value }
  } catch {
    // The optional host API must never prevent capture or saves.
    return {
      present: true,
      failed: true,
      known: Boolean(cached),
      value: cached?.value,
    }
  }
}

function stripExperiment(touch: AttributionTouch): AttributionTouch {
  return Object.fromEntries(
    Object.entries(touch).filter(
      ([key]) =>
        !experimentAttributionFields.includes(
          key as (typeof experimentAttributionFields)[number],
        ),
    ),
  ) as AttributionTouch
}

function stripAssignment(attribution: LeadAttribution): LeadAttribution | null {
  const firstTouch = parseAttributionTouch(
    stripExperiment(attribution.firstTouch),
  )
  const latestTouch = parseAttributionTouch(
    stripExperiment(attribution.latestTouch),
  )
  if (!firstTouch)
    return latestTouch ? { firstTouch: latestTouch, latestTouch } : null
  return { firstTouch, latestTouch: latestTouch ?? firstTouch }
}

function clearPersistedAttribution(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    /* Storage can be blocked. */
  }
  const host = window.location.hostname
  const domain =
    host === 'taxforce.ai' || host.endsWith('.taxforce.ai')
      ? '; Domain=.taxforce.ai'
      : ''
  try {
    document.cookie = `${COOKIE_NAME}=; Path=/; SameSite=Lax; Max-Age=0${domain}`
  } catch {
    /* Cookies can be blocked. */
  }
}

function clearPersistedExperiment(): LeadAttribution | null {
  const now = Date.now()
  const snapshots = readSnapshots()
    .map(parseLeadAttribution)
    .filter((snapshot) => snapshot !== null)
    .map(stripAssignment)
    .filter((snapshot) => snapshot !== null)
  const remaining = mergeLeadAttribution(snapshots, now - TTL_MS, now)
  // Remove the old cookie even when the sanitized replacement exceeds its size limit.
  clearPersistedAttribution()
  if (remaining) persist(remaining)
  return remaining
}

export function getLeadAttribution(
  hostInput?: unknown,
  experimentInput?: unknown,
): LeadAttribution | null {
  if (typeof window === 'undefined' || typeof document === 'undefined')
    return null
  const now = Date.now()
  const snapshots = readSnapshots()
  const hasStoredSnapshot = snapshots.some(
    (snapshot) => parseLeadAttribution(snapshot) !== null,
  )
  const touch = capturePage()
  // Maintain old Google fields and migrate an existing Google first touch.
  const google = getGoogleAdsAttribution()
  const stored = mergeLeadAttribution(snapshots, now - TTL_MS, now)
  if (
    !stored &&
    google?.capturedAt &&
    google.landingUrl &&
    (!touch || Date.parse(google.capturedAt) < Date.parse(touch.capturedAt))
  ) {
    try {
      const params = new URL(google.landingUrl).searchParams
      const touch = parseAttributionTouch({
        ...Object.fromEntries(
          attributionFields.map((field) => [field, params.get(field)]),
        ),
        ...google,
        capturedAt: google.capturedAt,
        landingUrl: google.landingUrl,
        captureMethod: 'legacy_google',
      })
      if (touch) snapshots.push({ firstTouch: touch, latestTouch: touch })
    } catch {
      // Ignore an invalid legacy landing URL and capture the current visit.
    }
  }
  const host = hostInput ?? readHostAttribution()
  const structuredHost = parseLeadAttribution(host)
  if (structuredHost) {
    snapshots.push(structuredHost)
  } else if (!hasStoredSnapshot && !touch && host && typeof host === 'object') {
    // The website's older API supplies only flat campaign fields. Capture the
    // observation now; do not invent the original click time or landing page.
    // Undated host data cannot establish a newer touch than a stored snapshot.
    const hostTouch = parseAttributionTouch({
      ...host,
      capturedAt: new Date().toISOString(),
      landingUrl: window.location.href,
      captureMethod: 'host',
    })
    const previous = (window as CaptureWindow).__taxforceAttributionPage
    const knownTouch = previous?.hostTouch
    if (hostTouch && (!knownTouch || !sameSignal(knownTouch, hostTouch))) {
      snapshots.push({ firstTouch: hostTouch, latestTouch: hostTouch })
      if (previous) previous.hostTouch = hostTouch
    } else if (previous?.hostTouch) {
      snapshots.push({
        firstTouch: previous.hostTouch,
        latestTouch: previous.hostTouch,
      })
    }
  }
  if (touch) snapshots.push({ firstTouch: touch, latestTouch: touch })
  const hostExperiment = readHostExperiment()
  const experiment = parseExperimentAttribution(
    hostExperiment.present
      ? hostExperiment.value
      : (experimentInput ?? readHostAttribution()),
  )
  if (
    !touch &&
    experiment &&
    !mergeLeadAttribution(snapshots, now - TTL_MS, Date.now())
  ) {
    const experimentTouch = parseAttributionTouch({
      ...experiment,
      capturedAt: new Date().toISOString(),
      landingUrl: window.location.href,
      captureMethod: 'host',
    })
    if (experimentTouch)
      snapshots.push({
        firstTouch: experimentTouch,
        latestTouch: experimentTouch,
      })
  }
  let attribution = mergeLeadAttribution(snapshots, now - TTL_MS, Date.now())
  if (attribution && (hostExperiment.present || experiment)) {
    attribution = getSubmissionAttribution(attribution, experimentInput)
  }
  if (attribution) persist(attribution)
  return attribution
}

/** Refresh only assignment metadata; keep campaign touches and their times. */
export function getSubmissionAttribution(
  attribution: LeadAttribution | null,
  experimentInput?: unknown,
): LeadAttribution | null {
  const hostExperiment = readHostExperiment()
  // The form can mount before the first rendered exposure. Capture on save
  // once consent and rendering make the assignment available.
  if (!attribution) {
    if (hostExperiment.failed && !hostExperiment.known)
      return mergeLeadAttribution(
        readSnapshots(),
        Date.now() - TTL_MS,
        Date.now(),
      )
    if (parseExperimentAttribution(hostExperiment.value))
      return getLeadAttribution()
    if (hostExperiment.present) return clearPersistedExperiment()
    return null
  }
  const retained = mergeLeadAttribution(
    [attribution, ...readSnapshots()],
    Date.now() - TTL_MS,
    Date.now(),
  )
  if (!retained) {
    clearPersistedAttribution()
    return null
  }
  if (hostExperiment.failed && !hostExperiment.known) {
    return retained
  }
  if (!hostExperiment.present && !experimentInput) return retained
  const experiment = parseExperimentAttribution(
    hostExperiment.present ? hostExperiment.value : experimentInput,
  )
  const next = {
    firstTouch: { ...stripExperiment(retained.firstTouch), ...experiment },
    latestTouch: { ...stripExperiment(retained.latestTouch), ...experiment },
  }
  // A consent withdrawal also removes assignment data from browser snapshots.
  const parsed = parseLeadAttribution(next) ?? stripAssignment(retained)
  if (!experiment) clearPersistedAttribution()
  if (parsed) persist(parsed)
  else clearPersistedAttribution()
  return parsed
}
